import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Sparkles from 'src/assets/svgs/Sparkles.svg'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Spotlight from 'src/components/Spotlight/Spotlight'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { useBackgroundDispatch, useCookieState } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  accountTransactionsUrl,
  accountVirtualCardsUrl,
  budgetOverviewUrl,
} from 'src/navigation/urls'
import {
  getCreditCardsStatisticsClearedTransactionCreditAmount,
  getCreditCardsStatisticsClearedTransactionDebitAmount,
} from 'src/redux/actions/data/creditCards.actions'
import { selectCreditCardClearedTransactionAmountFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { css } from 'styled-components'

interface BudgetVirtualCardsBoxProps {
  className?: string
  creditCard: CreditCardListModel
  vcnCount: number
}
const BudgetVirtualCardsBox: React.FC<BudgetVirtualCardsBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  const ccClearedTransactionAmountFromState = useSelector(
    selectCreditCardClearedTransactionAmountFromState,
  )

  const {
    cookieValue: showBudgetVirtualCardsPromo,
    updateCookieValue: setShowBudgetVirtualCardsPromo,
  } = useCookieState(
    CookieNames.AccountVirtualCards,
    CookieValuePaths.shouldShowBudgetVirtualCardsPromo,
    true,
  )

  useEffect((): void => {
    dispatch(
      getCreditCardsStatisticsClearedTransactionDebitAmount([
        props.creditCard.id,
      ]),
    )
    dispatch(
      getCreditCardsStatisticsClearedTransactionCreditAmount([
        props.creditCard.id,
      ]),
    )
  }, [props.creditCard?.status, props.creditCard.id])

  return (
    <Box noPadding>
      <div
        css={css`
          padding: 40px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {props.creditCard.recurs
              ? translate('Virtual Cards on Recurring Budget')
              : translate('Virtual Cards on Budget')}
            {showBudgetVirtualCardsPromo && (
              <div
                css={css`
                  position: relative;
                  width: 100%;
                `}
              >
                <Spotlight
                  imageSrc={Sparkles}
                  smallerText
                  tooltip
                  subtitle={translate(
                    '${bold} View the total amount that has been spent across all Virtual Cards on a Budget, and also get a better understanding of how much Credit is available to spend.',
                    {
                      bold: (
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              styleConstants.colors.white,
                              styleConstants.fontFamilies.medium,
                            )}
                          `}
                        >
                          {translate('New!')}
                        </span>
                      ),
                    },
                  )}
                  css={css`
                    position: absolute;
                    z-index: 3;
                    width: 320px;
                    top: 145px;
                  `}
                  cookieFunction={setShowBudgetVirtualCardsPromo}
                />
              </div>
            )}
          </span>
        </div>
        <span
          css={css`
            display: flex;
            justify-content: space-between;
            line-height: 125%;
            ${getFontCss(
              16,
              styleConstants.colors.font_gray_light,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          <span
            css={css`
              margin-top: 30px;
              flex-direction: column;
              width: 50%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {translate('Total Available Credit')}
              <div
                css={css`
                  display: inline-block;
                `}
                onMouseEnter={(): void => {
                  analytics?.track(
                    'budget detail overview - view available credit tooltip - button press',
                    {
                      intent: 'view available credit tooltip',
                      interaction: 'button press',
                      screen: budgetOverviewUrl(':id'),
                    },
                  )
                }}
              >
                <Tooltip
                  openLeft={false}
                  css={css`
                    z-index: 2;
                  `}
                >
                  <p>
                    {translate(
                      '${boldedText} is the amount left to be spent on virtual cards within this budget',
                      {
                        boldedText: (
                          <span
                            css={css`
                              font-weight: bold;
                            `}
                          >
                            {translate('Total Available Credit')}
                          </span>
                        ),
                      },
                    )}
                  </p>
                </Tooltip>
              </div>
            </div>
            <span
              css={css`
                display: block;
                ${getFontCss(
                  32,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )}
                line-height:125%;
                margin-top: 14px;
              `}
            >
              <Crncy
                currency={props.creditCard.currency}
                amount={
                  Math.round(
                    props.creditCard.totalActiveVirtualCardBalanceCents,
                  ) / 100
                }
              />
            </span>
            <span
              css={css`
                display: block;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.regular,
                )}
                line-height: 129%;
                margin-top: 8px;
              `}
            >
              {props.vcnCount === 0 ? (
                <span
                  css={css`
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  {translate('${num} Active Cards', {
                    num: '0',
                  })}
                </span>
              ) : (
                <HoverableLink
                  ariaLabelSource={`${props.vcnCount} Active Cards`}
                  to={accountVirtualCardsUrl(props.creditCard.id)}
                  regular
                >
                  <span
                    css={css`
                      display: flex;
                      flex-direction: row;
                      align-items: center;
                    `}
                  >
                    {props.vcnCount > 1
                      ? translate('${num} Active Cards', {
                          num: props.vcnCount.toString() || '0',
                        })
                      : translate('${num} Active Card', {
                          num: props.vcnCount.toString() || '0',
                        })}
                    <SvgArrowForward
                      css={css`
                        margin-left: 4px;
                      `}
                      style={{
                        transform: 'scale(0.667)',
                        transformOrigin: 'left',
                        flexShrink: 0,
                      }}
                    />
                  </span>
                </HoverableLink>
              )}
            </span>
          </span>
          <span
            css={css`
              margin-top: 30px;
              flex-direction: column;
              width: 50%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {translate('Total Spent')}
              <div
                css={css`
                  display: inline-block;
                `}
                onMouseEnter={(): void => {
                  analytics?.track(
                    'budget detail overview - view total spent tooltip - button press',
                    {
                      intent: 'view total spent tooltip',
                      interaction: 'button press',
                      screen: budgetOverviewUrl(':id'),
                    },
                  )
                }}
              >
                <Tooltip openLeft={false}>
                  <p>
                    {translate(
                      '${boldedText} is the total amount spent across all virtual cards within this budget',
                      {
                        boldedText: (
                          <span
                            css={css`
                              font-weight: bold;
                            `}
                          >
                            {translate('Total Spent')}
                          </span>
                        ),
                      },
                    )}
                  </p>
                </Tooltip>
              </div>
            </div>
            {ccClearedTransactionAmountFromState && (
              <span
                css={css`
                  display: block;
                  ${getFontCss(
                    32,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                  line-height:125%;
                  margin-top: 14px;
                `}
              >
                <Crncy
                  currency={props.creditCard.currency}
                  amount={
                    Math.round(
                      ccClearedTransactionAmountFromState[props.creditCard.id],
                    ) / 100
                  }
                />
              </span>
            )}
            <span
              css={css`
                display: block;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.regular,
                )}
                margin-top: 8px;
                line-height: 129%;
              `}
            >
              <HoverableLink
                ariaLabelSource={'View All Transactions'}
                to={accountTransactionsUrl(props.creditCard.id)}
                regular
                onClick={(): void => {
                  analytics?.track(
                    'budget detail overview - view all transactions - text link press',
                    {
                      intent: 'view all transactions',
                      interaction: 'button press',
                      screen: budgetOverviewUrl(':id'),
                    },
                  )
                }}
              >
                <span
                  css={css`
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                  `}
                >
                  {translate('View All Transactions')}
                  <SvgArrowForward
                    css={css`
                      margin-left: 4px;
                    `}
                    style={{
                      transform: 'scale(0.667)',
                      transformOrigin: 'left',
                      flexShrink: 0,
                    }}
                  />
                </span>
              </HoverableLink>
            </span>
          </span>
        </span>
      </div>
    </Box>
  )
}

export default BudgetVirtualCardsBox
